import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { useSEO } from "@/lib/seo";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";
const PLANS = [
  {
    name: "Free", price: "$0", cadence: "forever", key: "free",
    tagline: "Everything a solo freelancer needs to start.",
    features: ["Up to 5 active records", "Due-date & overdue tracking", "In-app Notification Center", "CSV export", "1 mobile number for optional alerts"],
    cta: "Start free", highlight: false,
  },
  {
    name: "Plus", price: "$9", cadence: "per month", key: "plus",
    tagline: "For freelancers juggling several clients.",
    features: ["Unlimited invoices & payouts", "Optional text alerts (all types)", "Weekly summary digest", "Recurring monthly invoices", "Reports & breakdowns"],
    cta: "Choose Plus", highlight: true,
  },
  {
    name: "Pro", price: "$19", cadence: "per month", key: "pro",
    tagline: "For contractors running a busy book.",
    features: ["Everything in Plus", "Priority email support", "Advanced report exports", "Snooze & smart reminders", "Early access to new features"],
    cta: "Choose Pro", highlight: false,
  },
];

const FAQ = [
  { q: "Does PaySignal collect payments?", a: "No. PaySignal does not process, hold, or collect money. It records what you enter and alerts you when a date passes — you mark things paid yourself." },
  { q: "Does PaySignal contact my clients?", a: "No. Alerts go to you only. Your clients and the platforms you work with never receive a message from PaySignal." },
  { q: "Is this a collections or debt service?", a: "No. PaySignal performs no collections, contacts no debtors, and never sells or refers accounts to anyone." },
  { q: "Are text alerts required on paid plans?", a: "No. SMS is optional on every plan, including paid ones. Declining texts does not change the price or restrict any other feature." },
  { q: "How do I stop the texts?", a: "Reply STOP to any message. You're removed from all sending immediately, and can reply START later to resubscribe." },
];

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loadingKey, setLoadingKey] = useState(null);

  useSEO({
    title: "Pricing — PaySignal Invoice & Payout Tracker",
    description: "Simple, honest pricing for freelancers and gig workers. Start free, or upgrade for unlimited records, text alerts, recurring invoices and reports. SMS is optional on every plan.",
    canonical: window.location.origin + "/pricing",
  });

  const startCheckout = async (planKey) => {
    if (!user) {
      navigate(`/signup?plan=${planKey}`);
      return;
    }
    setLoadingKey(planKey);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: planKey,
        origin_url: window.location.origin,
        user_id: user.id,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setLoadingKey(null);
    }
  };

  return (
    <div className="min-h-screen bg-cream text-espresso">
      <SiteHeader />

      <section className="max-w-3xl mx-auto px-6 pt-16 pb-8 text-center">
        <span className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.15em] uppercase text-brandblue bg-brandblue-light rounded-full px-3 py-1.5">
          <span className="w-2 h-2 rounded-full bg-lime" /> Pricing
        </span>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight mt-5 text-midnight leading-[1.05]" data-testid="pricing-title">
          Simple pricing. No surprises.
        </h1>
        <p className="text-lg text-ink-secondary mt-4">
          Start free and upgrade when you're ready.
        </p>
      </section>

      {/* Plans */}
      <section className="max-w-6xl mx-auto px-6 pb-6">
        <div className="grid md:grid-cols-3 gap-6 items-start">
          {PLANS.map((p) => (
            <div key={p.key} data-testid={`plan-${p.key}`}
              className={`rounded-3xl p-7 border ${p.highlight ? "bg-midnight text-white border-midnight shadow-lg md:-mt-3 md:mb-3" : "bg-white border-border"}`}>
              {p.highlight && <span className="inline-block text-[11px] font-bold uppercase tracking-wider text-midnight bg-lime rounded-full px-2.5 py-1 mb-3">Most popular</span>}
              <h3 className={`font-heading text-xl font-extrabold ${p.highlight ? "text-white" : "text-midnight"}`}>{p.name}</h3>
              <p className={`text-sm mt-1 ${p.highlight ? "text-white/70" : "text-ink-secondary"}`}>{p.tagline}</p>
              <div className="mt-5 flex items-end gap-1.5">
                <span className={`font-num font-extrabold text-4xl tracking-tight ${p.highlight ? "text-white" : "text-midnight"}`}>{p.price}</span>
                <span className={`text-sm mb-1 ${p.highlight ? "text-white/60" : "text-ink-muted"}`}>{p.cadence}</span>
              </div>
              {p.key === "free" ? (
                <Link to="/signup?plan=free">
                  <Button data-testid={`plan-cta-${p.key}`}
                    className="w-full mt-6" variant="outline">
                    {p.cta}
                  </Button>
                </Link>
              ) : (
                <Button data-testid={`plan-cta-${p.key}`}
                  onClick={() => startCheckout(p.key)}
                  disabled={loadingKey === p.key}
                  className={`w-full mt-6 ${p.highlight ? "bg-lime text-midnight hover:bg-lime-dark" : ""}`}
                  variant={p.highlight ? "default" : "outline"}>
                  {loadingKey === p.key ? <><Loader2 size={15} className="mr-1.5 animate-spin" /> Redirecting…</> : p.cta}
                </Button>
              )}
              <ul className="mt-6 space-y-2.5">
                {p.features.map((f) => (
                  <li key={f} className={`flex gap-2.5 text-sm ${p.highlight ? "text-white/90" : "text-ink-secondary"}`}>
                    <Check size={16} className={p.highlight ? "text-lime shrink-0 mt-0.5" : "text-status-success shrink-0 mt-0.5"} /> {f}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <p className="text-center text-sm text-ink-muted mt-8">
          Paid plans include a 14-day free trial. No card required to start on Free. Cancel anytime from your settings.
          Message & data rates from your carrier may apply to SMS.
        </p>
        <p className="text-center text-xs text-ink-muted mt-2" data-testid="pricing-sandbox-note">
          Payments run in Stripe test mode — use card <span className="font-semibold">4242 4242 4242 4242</span>, any future expiry and any CVC.
        </p>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6 py-16">
        <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tight text-midnight text-center">Questions, answered</h2>
        <div className="mt-8 space-y-3">
          {FAQ.map((item) => (
            <div key={item.q} className="border rounded-2xl bg-white p-5" data-testid="pricing-faq-item">
              <p className="font-heading font-bold text-midnight">{item.q}</p>
              <p className="text-sm text-ink-secondary mt-1.5 leading-relaxed">{item.a}</p>
            </div>
          ))}
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
